import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { AutoSizer } from 'react-virtualized';
import Popup from "../Popup.js";
import TextInput from "../TextInput.js";
import Pane from "../Pane.js";
import Content from "../Content.js";
import Flex from "../Flex.js";
import Button from "../Button.js";
import { sortDefault } from "./Sorting.js";
import Theme from "../Theme.js";
import S from '../TableV2.css';
import Checkbox from "../Checkbox.js";
import ToggleButton from "../ToggleButton.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function TableV2Filter({
  column,
  tableState,
  onChangeFilter: pOnChangeFilter,
  filterCheckboxClassName,
  preFilteredRows,
  filterButtonVariant,
  filterPaneVariant,
  filterButtonIconOn,
  filterButtonIconOff,
  filterButtonTextOn,
  filterButtonTextOff,
  filterClearButtonVariant,
  filterCancelButtonVariant,
  filterOkayButtonVariant,
  filterCancelButtonText = 'Cancel',
  filterOkayButtonText = 'Okay',
  filterCheckboxVariant,
  filterTextInputVariant,
  filterCheckboxLabelVariant
}) {
  const {
    t
  } = Theme.I18N.ns('tablev2.TableV2Filter');
  const {
    filters
  } = tableState;
  const [selected, setSelected] = useState({});
  const [filterValue, setFilterValue] = useState('');
  const options = useMemo(() => {
    const o = new Set();
    preFilteredRows.forEach(row => {
      o.add(row.values[column.id]);
    });
    return Array.from(o).filter(item => {
      const test = `${item}`.toLowerCase();
      return test.includes(filterValue.toLowerCase());
    }).sort(sortDefault);
  }, [preFilteredRows, filterValue]);
  const allSelected = useMemo(() => {
    return options.every(option => selected[option]);
  }, [options, selected]);
  const onSelectAll = useCallback(({
    value
  }) => {
    setSelected(options.reduce((prev, curr) => {
      prev[`${curr}`] = value;
      return prev;
    }, {}));
  }, [options]);
  const onClear = useCallback(() => {
    setSelected({});
    setFilterValue('');
  }, []);
  const onChangeFilterValue = useCallback(({
    value
  }) => {
    setFilterValue(value);
  }, [setFilterValue]);
  const isActive = useMemo(() => {
    return !!filters.find(filter => filter.id === column.id);
  }, [filters]);
  const onToggleOpen = useCallback(({
    isOpened
  }) => {
    if (isOpened) {
      const sel = options.reduce((prev, curr) => {
        const filterVals = column.filterValue || [];
        if (filterVals.includes(`${curr}`)) {
          prev[`${curr}`] = true;
        }
        return prev;
      }, {});
      setSelected(sel);
    }
  }, [setSelected, selected, filters, column, options]);
  const onSelectOption = useCallback(({
    value,
    data
  }) => {
    setSelected({
      ...selected,
      [`${data}`]: value
    });
  }, [setSelected, selected]);
  const renderFilterTrigger = useCallback(({
    ref,
    onToggle,
    isOpened
  }) => /*#__PURE__*/_jsx(ToggleButton, {
    className: isActive ? 'active' : '',
    variant: filterButtonVariant,
    iconOn: filterButtonIconOn,
    iconOff: filterButtonIconOff,
    on: filterButtonTextOn,
    off: filterButtonTextOff,
    value: isOpened,
    domRef: ref,
    onChange: onToggle
  }), [filterButtonIconOn, filterButtonIconOff, filterButtonTextOn, filterButtonTextOff, filterButtonVariant, isActive]);
  const renderFilterPane = useCallback(onClose => {
    const onChangeFilter = () => {
      const value = Object.entries(selected).filter(val => val[1]).map(val => val[0]);
      pOnChangeFilter({
        value
      });
      onClose();
    };
    return /*#__PURE__*/_jsx(Pane, {
      onClick: e => e.stopPropagation(),
      variant: filterPaneVariant,
      children: /*#__PURE__*/_jsxs(Content, {
        relative: true,
        children: [/*#__PURE__*/_jsx(Content.Header, {
          children: /*#__PURE__*/_jsx(Flex, {
            children: /*#__PURE__*/_jsx(TextInput, {
              variant: filterTextInputVariant,
              className: S.valueFilter,
              value: filterValue,
              alwaysFireOnReturn: false,
              changeOnReturn: false,
              hint: t('hint', 'Search by...'),
              autoFocus: true,
              onChange: onChangeFilterValue
            })
          })
        }), /*#__PURE__*/_jsxs(Content.Area, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            style: {
              margin: '8px 0'
            },
            justify: "space-between",
            align: "center",
            children: [/*#__PURE__*/_jsx(Checkbox, {
              value: allSelected,
              onChange: onSelectAll,
              variant: filterCheckboxVariant,
              labelVariant: filterCheckboxLabelVariant,
              label: t('label', 'Select All')
            }), /*#__PURE__*/_jsx(Button, {
              onClick: onClear,
              variant: filterClearButtonVariant,
              children: t('clear', 'Clear')
            })]
          }), /*#__PURE__*/_jsx("hr", {}), /*#__PURE__*/_jsx(FilterOptionList, {
            checkboxVariant: filterCheckboxVariant,
            filterCheckboxClassName: filterCheckboxClassName,
            checkboxLabelVariant: filterCheckboxLabelVariant,
            onSelectOption: onSelectOption,
            options: options,
            selected: selected
          })]
        }), /*#__PURE__*/_jsx(Content.Footer, {
          children: /*#__PURE__*/_jsxs(Flex, {
            justify: "flex-end",
            variant: "tableFilterButtonFlex|rowM",
            children: [/*#__PURE__*/_jsx(Button, {
              variant: filterCancelButtonVariant,
              onClick: onClose,
              children: t('filterCancelButtonText', filterCancelButtonText)
            }), /*#__PURE__*/_jsx(Button, {
              variant: filterOkayButtonVariant,
              onClick: onChangeFilter,
              children: t('filterOkayButtonText', filterOkayButtonText)
            })]
          })
        })]
      })
    });
  }, [column, pOnChangeFilter, onChangeFilterValue, filterValue, options, selected, filterCancelButtonVariant, filterOkayButtonVariant, filterCheckboxVariant, filterClearButtonVariant, filterPaneVariant, filterTextInputVariant]);
  return /*#__PURE__*/_jsx(Popup, {
    attachment: "top left",
    targetAttachment: "bottom left",
    constraints: [{
      to: 'scrollParent',
      pin: false
    }, {
      to: 'window',
      pin: true
    }],
    onChange: onToggleOpen,
    render: renderFilterPane,
    trigger: renderFilterTrigger
  });
}
function FilterOptionList({
  options,
  selected,
  onSelectOption,
  checkboxVariant,
  filterCheckboxClassName,
  checkboxLabelVariant
}) {
  const RowRenderer = useCallback(({
    index,
    style
  }) => {
    const option = options[index];
    return /*#__PURE__*/_jsx(Flex, {
      variant: "rowM",
      style: style,
      children: /*#__PURE__*/_jsx(Checkbox, {
        className: filterCheckboxClassName,
        variant: checkboxVariant,
        label: option,
        labelVariant: checkboxLabelVariant,
        value: selected[`${option}`],
        eventData: option,
        onChange: onSelectOption
      })
    });
  }, [options, selected]);
  return /*#__PURE__*/_jsx(AutoSizer, {
    disableHeight: true,
    children: ({
      width
    }) => {
      return /*#__PURE__*/_jsx(FixedSizeList, {
        itemCount: options.length,
        itemSize: 35,
        height: 238,
        width: width,
        children: RowRenderer
      });
    }
  });
}